import React, { useState, useEffect } from 'react';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Divider,
  Button,
  Chip,
  Avatar
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import axiosInstance from '../services/api';

const AlertBadge = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchRecentAlerts();
    // Refresh alerts every 5 minutes
    const interval = setInterval(fetchRecentAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchRecentAlerts = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/alerts/recent');
      setAlerts(response.data);
      setUnreadCount(response.data.filter(alert => !alert.notifications?.ui?.seen).length);
    } catch (error) {
      console.error('Lỗi lấy cảnh báo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await axiosInstance.patch(`/alerts/history/${alertId}/acknowledge`);
      fetchRecentAlerts(); // Refresh alerts
    } catch (error) {
      console.error('Lỗi xác nhận cảnh báo:', error);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon color="error" />;
      case 'high':
        return <WarningIcon color="warning" />;
      case 'medium':
        return <InfoIcon color="info" />;
      default:
        return <InfoIcon color="action" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'traffic_drop':
        return <TrendingDownIcon />;
      case 'traffic_spike':
        return <TrendingUpIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{ mr: 1 }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { width: 400, maxHeight: 500 }
        }}
      >
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            🔔 Cảnh báo gần đây
          </Typography>
          
          {loading && (
            <Typography variant="body2" color="textSecondary" align="center">
              Đang tải...
            </Typography>
          )}

          {!loading && alerts.length === 0 && (
            <Box py={2} textAlign="center">
              <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="body1" color="textSecondary">
                Không có cảnh báo nào
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Tất cả website đang hoạt động tốt
              </Typography>
            </Box>
          )}

          {!loading && alerts.length > 0 && (
            <List dense>
              {alerts.slice(0, 5).map((alert, index) => (
                <React.Fragment key={alert._id}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      bgcolor: alert.notifications?.ui?.seen ? 'transparent' : 'action.hover',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <ListItemIcon>
                      <Avatar
                        sx={{
                          bgcolor: `${getSeverityColor(alert.severity)}.light`,
                          width: 32,
                          height: 32
                        }}
                      >
                        {getSeverityIcon(alert.severity)}
                      </Avatar>
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Typography variant="subtitle2" component="div" sx={{ fontWeight: 'bold' }}>
                            {alert.title}
                          </Typography>
                          <Chip
                            label={alert.severity.toUpperCase()}
                            color={getSeverityColor(alert.severity)}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            Website: {alert.websiteId?.name || alert.websiteId?.url}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            {alert.message}
                          </Typography>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                            <Typography variant="caption" color="textSecondary">
                              {formatTime(alert.createdAt)}
                            </Typography>
                            {!alert.notifications?.ui?.seen && (
                              <Button
                                size="small"
                                onClick={() => handleAcknowledgeAlert(alert._id)}
                                variant="outlined"
                              >
                                Xác nhận
                              </Button>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < alerts.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}

          <Divider sx={{ my: 1 }} />
          
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Button
              size="small"
              onClick={() => {
                window.location.href = '/admin/alerts';
                handleClose();
              }}
            >
              Xem tất cả
            </Button>
            <Button
              size="small"
              onClick={() => {
                fetchRecentAlerts();
              }}
            >
              Làm mới
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default AlertBadge; 